/**
 * Vanilla JavaScript Autocomplete Module
 * A lightweight, dependency-free autocomplete/suggestion component
 */

const DEFAULT_OPTIONS = {
  minChars: 3,
  debounceMs: 300,
  maxResults: 100,
  displayKey: 'name',
  valueKey: 'id',
  // Nombre de résultats affichés avant le bouton « Afficher tous les résultats »
  // (null = tout afficher directement, sans bouton)
  collapsedCount: null,
  showAllLabel: 'Afficher tous les résultats',
};

/**
 * Creates an autocomplete instance for the given input
 * @param {Object} config - Configuration object
 * @param {string} config.selector - CSS selector for the input element
 * @param {string|Function} config.endpoint - URL endpoint for fetching suggestions (use {query} as placeholder) or function returning a URL
 * @param {Function} [config.onSelect] - Callback when a suggestion is selected, receives the selected item
 * @param {Function} [config.filterResults] - Optional callback to filter fetched suggestions before rendering
 * @param {Object} [config.options] - Additional options
 * @param {number} [config.options.minChars=3] - Minimum characters before searching
 * @param {number} [config.options.debounceMs=300] - Debounce delay in milliseconds
 * @param {number} [config.options.maxResults=100] - Maximum number of results to display
 * @param {string} [config.options.displayKey='name'] - Key to use for displaying suggestions
 * @param {string} [config.options.valueKey='id'] - Key to use for the value
 * @returns {Object|null} - Autocomplete instance or null if input not found
 */
export const createAutocomplete = ({ selector, endpoint, onSelect, filterResults, options = {} }) => {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  let debounceTimer = null;
  let resultsContainer = null;
  let activeIndex = -1;
  let currentResults = [];
  let input = null;
  let expanded = false;

  const cleanup = () => {
    if (resultsContainer) {
      resultsContainer.remove();
      resultsContainer = null;
    }
    activeIndex = -1;
    currentResults = [];
  };

  const createResultsContainer = () => {
    const container = document.createElement('ul');
    container.className = 'autocomplete-results';
    container.setAttribute('role', 'listbox');

    input.parentElement.appendChild(container);
    return container;
  };

  const setActiveItem = (index) => {
    const items = resultsContainer?.querySelectorAll('li[role="option"]');
    if (!items) return;

    items.forEach((item, i) => {
      if (i === index) {
        item.setAttribute('aria-selected', 'true');
      } else {
        item.setAttribute('aria-selected', 'false');
      }
    });

    activeIndex = index;
  };

  const hideResults = () => {
    if (resultsContainer) {
      resultsContainer.style.display = 'none';
    }
    activeIndex = -1;
    input?.setAttribute('aria-expanded', 'false');
  };

  const selectItem = (item) => {
    input.value = item[opts.displayKey];
    hideResults();
    if (onSelect && typeof onSelect === 'function') {
      onSelect(item);
    }
  };

  const drawResults = () => {
    resultsContainer.innerHTML = '';
    activeIndex = -1;

    const collapsed = opts.collapsedCount && !expanded && currentResults.length > opts.collapsedCount;
    const visibleResults = currentResults.slice(0, collapsed ? opts.collapsedCount : opts.maxResults);

    visibleResults.forEach((item, index) => {
      const li = document.createElement('li');
      li.setAttribute('role', 'option');
      li.setAttribute('data-index', index);
      li.textContent = item[opts.displayKey];

      li.addEventListener('mouseenter', () => {
        setActiveItem(index);
      });

      li.addEventListener('click', (e) => {
        e.preventDefault();
        selectItem(item);
      });

      resultsContainer.appendChild(li);
    });

    if (collapsed) {
      const li = document.createElement('li');
      li.className = 'autocomplete-show-all';

      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = opts.showAllLabel;
      // mousedown + preventDefault : ne pas voler le focus de l'input, sinon son
      // blur (timeout 200 ms) referme la liste juste après l'avoir dépliée
      button.addEventListener('mousedown', (e) => {
        e.preventDefault();
        expanded = true;
        drawResults();
      });

      li.appendChild(button);
      resultsContainer.appendChild(li);
    }
  };

  const renderResults = (results) => {
    if (!resultsContainer) {
      resultsContainer = createResultsContainer();
    }

    currentResults = results;
    expanded = false;

    if (results.length === 0) {
      resultsContainer.innerHTML = '';
      resultsContainer.style.display = 'none';
      activeIndex = -1;
      input.setAttribute('aria-expanded', 'false');
      return;
    }

    drawResults();
    resultsContainer.style.display = 'block';
    input.setAttribute('aria-expanded', 'true');
  };

  const handleKeydown = (e) => {
    if (!resultsContainer || resultsContainer.style.display === 'none') return;

    const items = resultsContainer.querySelectorAll('li[role="option"]');
    if (items.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveItem(Math.min(activeIndex + 1, items.length - 1));
        items[activeIndex]?.scrollIntoView({ block: 'nearest' });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveItem(Math.max(activeIndex - 1, 0));
        items[activeIndex]?.scrollIntoView({ block: 'nearest' });
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && currentResults[activeIndex]) {
          selectItem(currentResults[activeIndex]);
        } else if (currentResults[0]) {
          selectItem(currentResults[0]);
        }
        break;
      case 'Escape':
        hideResults();
        break;
    }
  };

  const buildEndpointUrl = (query) => {
    if (typeof endpoint === 'function') {
      return endpoint(query, input);
    }

    if (endpoint.includes('{query}')) {
      return endpoint.replace('{query}', encodeURIComponent(query));
    }

    return `${endpoint}?q=${encodeURIComponent(query)}`;
  };

  const fetchResults = async (query) => {
    try {
      const url = buildEndpointUrl(query);

      const response = await fetch(url);
      if (!response.ok) {
        console.error('Autocomplete fetch error: Network response was not ok');
        hideResults();
        return;
      }
      const data = await response.json();
      const filteredData = typeof filterResults === 'function' ? filterResults(data, input) : data;
      renderResults(Array.isArray(filteredData) ? filteredData : []);
    } catch (error) {
      console.error('Autocomplete fetch error:', error);
      hideResults();
    }
  };

  const handleInput = () => {
    const query = input.value.trim();

    if (query.length < opts.minChars) {
      hideResults();
      return;
    }

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      fetchResults(query);
    }, opts.debounceMs);
  };

  const init = () => {
    input = document.querySelector(selector);

    if (!input) {
      console.warn(`Autocomplete: Input element not found for selector "${selector}"`);
      return null;
    }

    // Set up ARIA attributes for accessibility
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');

    input.addEventListener('input', handleInput);
    input.addEventListener('keydown', handleKeydown);
    input.addEventListener('search', () => {
      if (input.value === '') {
        hideResults();
      }
    });
    input.addEventListener('blur', () => {
      // Delay hiding to allow click events on results
      setTimeout(hideResults, 200);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= opts.minChars) {
        handleInput();
      }
    });

    // Close results when clicking outside
    document.addEventListener('click', (e) => {
      if (!input.contains(e.target) && !resultsContainer?.contains(e.target)) {
        hideResults();
      }
    });

    return instance;
  };

  // Public API
  const instance = {
    init,
    cleanup,
    hideResults,
    getInput: () => input,
    getResultsContainer: () => resultsContainer,
  };

  return init();
};

/**
 * Helper to manage autocomplete lifecycle with Turbo
 * @param {Object} config - Same config as createAutocomplete
 * @returns {Object} - Object with destroy method
 */
export const createTurboAutocomplete = (config) => {
  let autocompleteInstance = null;

  const initialize = () => {
    if (autocompleteInstance) {
      return;
    }
    autocompleteInstance = createAutocomplete(config);
  };

  const destroy = () => {
    if (autocompleteInstance) {
      autocompleteInstance.cleanup();
      autocompleteInstance = null;
    }
  };

  // Set up event listeners
  window.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('turbo:load', initialize);
  document.addEventListener('turbo:before-cache', destroy);

  // Handle browser back/forward cache (bfcache) restoration
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      destroy();
      initialize();
    }
  });

  // Initialize immediately if DOM is already loaded
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initialize();
  }

  return {
    destroy,
    getInstance: () => autocompleteInstance,
  };
};

export default { createAutocomplete, createTurboAutocomplete };
